import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Edit, Trash2, Settings } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';

const API_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.acbase.org').replace(/\/$/, '');

export default function ConfigurationsPage() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('countries');
  
  // States per ogni tabella
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [companySectors, setCompanySectors] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [parameters, setParameters] = useState([]);
  
  // Stati per modali
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Loading states
  const [loading, setLoading] = useState(false);
  
  const canView = hasPermission('CONFIG_VIEW');
  const canEdit = hasPermission('CONFIG_EDIT');
  const canManageSystemCategories = hasPermission('ADMIN_CATEGORIE_SISTEMA');

  useEffect(() => {
    if (canView) {
      fetchAllData();
    }
  }, [canView, canManageSystemCategories]);

  const fetchAllData = async () => {
    await Promise.all([
      fetchCountries(),
      fetchProvinces(),
      fetchCities(),
      fetchJobCategories(),
      fetchCompanySectors(),
      fetchCustomRoles(),
      canManageSystemCategories ? fetchParameters() : Promise.resolve()
    ]);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/countries`, {
        headers: getAuthHeader()
      });
      setCountries(res.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchProvinces = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/provinces`, {
        headers: getAuthHeader()
      });
      setProvinces(res.data);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/cities`, {
        headers: getAuthHeader()
      });
      setCities(res.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchJobCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/job-categories`, {
        headers: getAuthHeader()
      });
      setJobCategories(res.data);
    } catch (error) {
      console.error('Error fetching job categories:', error);
    }
  };

  const fetchCompanySectors = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/company-sectors`, {
        headers: getAuthHeader()
      });
      setCompanySectors(res.data);
    } catch (error) {
      console.error('Error fetching company sectors:', error);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/custom-roles`, {
        headers: getAuthHeader()
      });
      setCustomRoles(res.data);
    } catch (error) {
      console.error('Error fetching custom roles:', error);
    }
  };

  const fetchParameters = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/config/parameters`, {
        headers: getAuthHeader()
      });
      setParameters(res.data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
    }
  };

  const openCreateModal = (type) => {
    setModalMode('create');
    setCurrentItem(null);
    setFormData(getEmptyFormData(type));
    setIsModalOpen(true);
  };

  const openEditModal = (type, item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({ ...item });
    setIsModalOpen(true);
  };

  const getEmptyFormData = (type) => {
    switch (activeTab) {
      case 'countries':
        return { name: '', code: '' };
      case 'provinces':
        return { name: '', code: '', country_id: '' };
      case 'cities':
        return { name: '', province_id: '' };
      case 'job-categories':
      case 'company-sectors':
      case 'custom-roles':
        return { name: '' };
      case 'parameters':
        return { chiave: '', valore: '', tipo: 'string' };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const endpoint = getEndpoint();
      const baseUrl = activeTab === 'custom-roles' ? `${API_URL}/api` : `${API_URL}/api/config`;
      
      if (modalMode === 'create') {
        await axios.post(`${baseUrl}/${endpoint}`, formData, {
          headers: getAuthHeader()
        });
      } else {
        if (activeTab === 'parameters') {
          await axios.put(`${baseUrl}/${endpoint}/${encodeURIComponent(currentItem.chiave)}`, {
            valore: formData.valore
          }, {
            headers: getAuthHeader()
          });
        } else {
          await axios.put(`${baseUrl}/${endpoint}/${currentItem.id}`, formData, {
            headers: getAuthHeader()
          });
        }
      }
      
      // Refresh data
      await fetchDataForActiveTab();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving:', error);
      alert('Errore durante il salvataggio: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo elemento?')) return;
    
    setLoading(true);
    try {
      const endpoint = getEndpoint();
      const baseUrl = activeTab === 'custom-roles' ? `${API_URL}/api` : `${API_URL}/api/config`;
      await axios.delete(`${baseUrl}/${endpoint}/${id}`, {
        headers: getAuthHeader()
      });
      await fetchDataForActiveTab();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Errore durante l\'eliminazione: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = () => {
    switch (activeTab) {
      case 'countries': return 'countries';
      case 'provinces': return 'provinces';
      case 'cities': return 'cities';
      case 'job-categories': return 'job-categories';
      case 'company-sectors': return 'company-sectors';
      case 'custom-roles': return 'custom-roles';
      case 'parameters': return 'parameters';
      default: return '';
    }
  };

  const fetchDataForActiveTab = async () => {
    switch (activeTab) {
      case 'countries': return fetchCountries();
      case 'provinces': return fetchProvinces();
      case 'cities': return fetchCities();
      case 'job-categories': return fetchJobCategories();
      case 'company-sectors': return fetchCompanySectors();
      case 'custom-roles': return fetchCustomRoles();
      case 'parameters': return fetchParameters();
      default: return Promise.resolve();
    }
  };

  const renderCountriesTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Nazioni</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('countries')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi nazione
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Codice</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {countries.map((country) => (
            <TableRow key={country.id}>
              <TableCell>{country.name}</TableCell>
              <TableCell>{country.code}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('countries', country)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(country.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderProvincesTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Province</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('provinces')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi provincia
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Codice</TableHead>
            <TableHead>Nazione</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {provinces.map((province) => (
            <TableRow key={province.id}>
              <TableCell>{province.name}</TableCell>
              <TableCell>{province.code}</TableCell>
              <TableCell>{province.country_name}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('provinces', province)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(province.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderCitiesTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Città</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('cities')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi città
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Provincia</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {cities.map((city) => (
            <TableRow key={city.id}>
              <TableCell>{city.name}</TableCell>
              <TableCell>{city.province_name}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('cities', city)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(city.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderJobCategoriesTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Categorie professionali</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('job-categories')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi categoria
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobCategories.map((category) => (
            <TableRow key={category.id}>
              <TableCell>{category.name}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('job-categories', category)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderCompanySectorsTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Settori aziendali</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('company-sectors')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi settore
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {companySectors.map((sector) => (
            <TableRow key={sector.id}>
              <TableCell>{sector.name}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('company-sectors', sector)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(sector.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderCustomRolesTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Ruoli personalizzati</h3>
        {canEdit && (
          <Button onClick={() => openCreateModal('custom-roles')}>
            <Plus className="mr-2 h-4 w-4" /> Aggiungi ruolo
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {customRoles.map((role) => (
            <TableRow key={role.id}>
              <TableCell>{role.name}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('custom-roles', role)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(role.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderParametersTable = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Parametri</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Chiave</TableHead>
            <TableHead>Valore</TableHead>
            <TableHead>Tipo</TableHead>
            {canEdit && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {parameters.map((param) => (
            <TableRow key={param.chiave}>
              <TableCell className="font-mono text-xs">{param.chiave}</TableCell>
              <TableCell>{String(param.valore ?? '')}</TableCell>
              <TableCell>{param.tipo}</TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal('parameters', param)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-gray-500">
        I parametri sono predefiniti: da questa schermata è possibile modificare solo il valore.
      </p>
    </div>
  );

  const renderModal = () => {
    return (
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {modalMode === 'create' ? 'Crea' : 'Modifica'} {getModalTitle()}
            </DialogTitle>
            <DialogDescription>
              Inserisci i dati richiesti e salva.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {activeTab === 'countries' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Codice ISO (2 lettere)</Label>
                  <Input
                    id="code"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    maxLength={2}
                  />
                </div>
              </>
            )}
            
            {activeTab === 'provinces' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Codice (es: RM, MI)</Label>
                  <Input
                    id="code"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country_id">Nazione</Label>
                  <Select
                    value={formData.country_id?.toString() || ''}
                    onValueChange={(value) => setFormData({ ...formData, country_id: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona nazione" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.id} value={country.id.toString()}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {activeTab === 'cities' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="province_id">Provincia</Label>
                  <Select
                    value={formData.province_id?.toString() || ''}
                    onValueChange={(value) => setFormData({ ...formData, province_id: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona provincia" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((province) => (
                        <SelectItem key={province.id} value={province.id.toString()}>
                          {province.name} ({province.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {(activeTab === 'job-categories' || activeTab === 'company-sectors' || activeTab === 'custom-roles') && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            )}

            {activeTab === 'parameters' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="chiave">Chiave</Label>
                  <Input
                    id="chiave"
                    value={formData.chiave || ''}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo</Label>
                  <Input
                    id="tipo"
                    value={formData.tipo || ''}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valore">Valore</Label>
                  {formData.tipo === 'boolean' ? (
                    <Select
                      value={(String(formData.valore).toLowerCase() === 'true' || String(formData.valore) === '1') ? 'true' : 'false'}
                      onValueChange={(value) => setFormData({ ...formData, valore: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">true</SelectItem>
                        <SelectItem value="false">false</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="valore"
                      type={formData.tipo === 'integer' || formData.tipo === 'decimal' ? 'number' : 'text'}
                      step={formData.tipo === 'decimal' ? 'any' : formData.tipo === 'integer' ? '1' : undefined}
                      value={formData.valore ?? ''}
                      onChange={(e) => setFormData({ ...formData, valore: e.target.value })}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const getModalTitle = () => {
    switch (activeTab) {
      case 'countries': return 'Nazione';
      case 'provinces': return 'Provincia';
      case 'cities': return 'Città';
      case 'job-categories': return 'Categoria professionale';
      case 'company-sectors': return 'Settore aziendale';
      case 'custom-roles': return 'Ruolo personalizzato';
      case 'parameters': return 'Parametro';
      default: return '';
    }
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare le configurazioni." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-20 px-6 max-w-7xl mx-auto pb-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-6 w-6" />
            <h1 className="text-3xl font-bold">Configurazioni di sistema</h1>
          </div>
          <p className="text-gray-600">
            Gestisci le tabelle di configurazione per menu a tendina e lookup
          </p>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${canManageSystemCategories ? 'grid-cols-7' : 'grid-cols-6'}`}>
          <TabsTrigger value="countries">Nazioni</TabsTrigger>
          <TabsTrigger value="provinces">Province</TabsTrigger>
          <TabsTrigger value="cities">Città</TabsTrigger>
          <TabsTrigger value="job-categories">Categorie</TabsTrigger>
          <TabsTrigger value="company-sectors">Settori</TabsTrigger>
          <TabsTrigger value="custom-roles">Ruoli</TabsTrigger>
          {canManageSystemCategories && <TabsTrigger value="parameters">Parametri</TabsTrigger>}
        </TabsList>
        
        <TabsContent value="countries" className="mt-6">
          {renderCountriesTable()}
        </TabsContent>
        
        <TabsContent value="provinces" className="mt-6">
          {renderProvincesTable()}
        </TabsContent>
        
        <TabsContent value="cities" className="mt-6">
          {renderCitiesTable()}
        </TabsContent>
        
        <TabsContent value="job-categories" className="mt-6">
          {renderJobCategoriesTable()}
        </TabsContent>
        
        <TabsContent value="company-sectors" className="mt-6">
          {renderCompanySectorsTable()}
        </TabsContent>

        <TabsContent value="custom-roles" className="mt-6">
          {renderCustomRolesTable()}
        </TabsContent>

        {canManageSystemCategories && (
          <TabsContent value="parameters" className="mt-6">
            {renderParametersTable()}
          </TabsContent>
        )}
      </Tabs>

      {renderModal()}
      </div>
    </div>
  );
}
